import { apiFetch } from "../api.js";
import type {
  NetWorthListResponse,
  NetWorthSnapshotResponse,
  NetWorthWindow,
} from "./types.js";

/**
 * Net-worth API module — Phase 4 Session 1 + Phase 16d additions.
 *
 * - `getNetWorth(window)`: returns time series + latest snapshot.
 *   Default window is `"24m"`; the backend short-circuits unknown
 *   windows to 400.
 * - `getNetWorthAsOf(date)`: returns one snapshot for the given date
 *   or 404 if missing. No nearest-neighbor fallback in v1.
 * - `getNetWorthMetrics(window)` (Phase 16d): derived return metrics
 *   over the requested window (Total / TWR / IRR / Max DD / Sharpe).
 *   Sister endpoint to `getNetWorth`; SPA fetches both in parallel.
 * - `getIndexHistory(ticker, startDate, endDate)` (Phase 16d): daily
 *   close-cents series for an ETF, used for chart overlays. Reuses
 *   the existing `/v1/prices/{ticker}` endpoint — no new route.
 *
 * All endpoints are JWT-authed; the household is resolved server-side
 * from the JWT's `primary_household_id` claim. Index history is tenant-
 * shared market data.
 */

export function getNetWorth(
  window: NetWorthWindow = "24m",
): Promise<NetWorthListResponse> {
  const qs = new URLSearchParams({ window });
  return apiFetch<NetWorthListResponse>(`/v1/networth?${qs.toString()}`);
}

export function getNetWorthAsOf(
  date: string,
): Promise<NetWorthSnapshotResponse> {
  const qs = new URLSearchParams({ asOf: date });
  return apiFetch<NetWorthSnapshotResponse>(
    `/v1/networth?${qs.toString()}`,
  );
}

// ─── Phase 16d additions ─────────────────────────────────────────────────

export interface NetWorthMetricsResponse {
  window: NetWorthWindow;
  period_days: number;
  start_date?: string;
  end_date?: string;
  start_value_cents?: number;
  end_value_cents?: number;
  total_pct: number;
  twr_pct: number;
  irr_pct: number | null;
  max_drawdown_pct: number;
  sharpe_pct: number | null;
  sharpe_risk_free_rate_pct?: number;
  cashflows_attributed: number;
  snapshots_in_window: number;
  methodology?: string;
  message?: string;
}

export function getNetWorthMetrics(
  window: NetWorthWindow = "24m",
): Promise<NetWorthMetricsResponse> {
  const qs = new URLSearchParams({ window });
  return apiFetch<NetWorthMetricsResponse>(
    `/v1/networth/metrics?${qs.toString()}`,
  );
}

export interface IndexHistoryBar {
  date: string;
  open?: number;
  high?: number;
  low?: number;
  close: number;
  volume?: number;
}

export interface IndexHistoryResponse {
  ticker: string;
  startDate: string;
  endDate: string;
  bars: IndexHistoryBar[];
  count: number;
}

/**
 * Daily close series for an index/ETF over a date range. Reuses the
 * existing `/v1/prices/{ticker}` handler. The SPA caches via TanStack
 * Query with a 24h staleTime — ETF closes don't change post-market.
 */
export function getIndexHistory(
  ticker: string,
  startDate: string,
  endDate: string,
): Promise<IndexHistoryResponse> {
  const qs = new URLSearchParams({ startDate, endDate });
  return apiFetch<IndexHistoryResponse>(
    `/v1/prices/${encodeURIComponent(ticker)}?${qs.toString()}`,
  );
}
